import { localized, msg } from '@lit/localize';
import { css, html, LitElement } from 'lit';
import { customElement } from 'lit/decorators.js';

import '@spectrum-web-components/button/sp-button.js';
import '@spectrum-web-components/dialog/sp-dialog.js';
import '@spectrum-web-components/underlay/sp-underlay.js';

@customElement('kv-confirmation-dialog')
@localized()
export class KvConfirmationDialog extends LitElement {
  static properties = {
    heading: { type: String },
    cancelLabel: { type: String, attribute: 'cancel-label' },
    confirmLabel: { type: String, attribute: 'confirm-label' },
    confirming: { type: Boolean },
  };

  static styles = css`
    :host {
      box-sizing: border-box;
      display: grid;
      inset: 0;
      padding: var(--gap-4);
      place-items: center;
      position: fixed;
      z-index: 10000;
      --kv-dialog-bg: var(--color-card-bg, var(--color-sidebar-bg, #fff));
    }

    sp-underlay {
      background-color: var(--color-modal-overlay);
      z-index: 1;
    }

    .dialog-container {
      box-sizing: border-box;
      min-width: 0;
      position: relative;
      width: min(520px, calc(100vw - 2 * var(--gap-4)));
      z-index: 2;
    }

    sp-dialog {
      background: var(--kv-dialog-bg);
      width: 100%;
    }

    .dialog-body {
      color: var(--color-app-text-secondary);
      font-size: 14px;
      line-height: 1.45;
    }
  `;

  declare heading: string;
  declare cancelLabel: string;
  declare confirmLabel: string;
  declare confirming: boolean;

  constructor() {
    super();
    this.heading = msg('Confirm action');
    this.cancelLabel = msg('Cancel');
    this.confirmLabel = msg('Confirm');
    this.confirming = false;
  }

  connectedCallback(): void {
    super.connectedCallback();
    window.addEventListener('keydown', this.onKeydown);
  }

  disconnectedCallback(): void {
    window.removeEventListener('keydown', this.onKeydown);
    super.disconnectedCallback();
  }

  private onKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && !this.confirming) {
      this.cancel();
    }
  };

  private cancel() {
    this.dispatchEvent(
      new CustomEvent('cancel', { bubbles: true, composed: true })
    );
  }

  private dispatchConfirm() {
    this.dispatchEvent(
      new CustomEvent('confirm', { bubbles: true, composed: true })
    );
  }

  render() {
    return html`
      <sp-underlay open @click=${this.cancel}></sp-underlay>
      <div class="dialog-container">
        <sp-dialog open underlay size="m" role="alertdialog">
          <h2 slot="heading">${this.heading}</h2>
          <div class="dialog-body">
            <slot></slot>
          </div>
          <sp-button
            slot="button"
            variant="secondary"
            ?disabled=${this.confirming}
            @click=${this.cancel}
          >
            ${this.cancelLabel}
          </sp-button>
          <sp-button
            slot="button"
            variant="negative"
            ?disabled=${this.confirming}
            @click=${this.dispatchConfirm}
          >
            ${this.confirmLabel}
          </sp-button>
        </sp-dialog>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'kv-confirmation-dialog': KvConfirmationDialog;
  }
}
